import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Wordmark } from "./Navbar";
import { Footer } from "./Footer";

const Layout = ({ title, updated, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="bg-[#111513] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="/" data-testid="legal-brand-wordmark" aria-label="Choose Me Properties — home">
            <Wordmark light />
          </a>
          <a
            href="/"
            data-testid="legal-back-home-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to home
          </a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#111513] tracking-tight">{title}</h1>
        <p className="mt-3 text-sm text-slate-500">Last updated: {updated}</p>
        <div className="mt-10 space-y-9">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#111513]">{title}</h2>
    <div className="mt-3 space-y-3 text-base text-[#3E4742] leading-relaxed">{children}</div>
  </section>
);

export const PrivacyPage = () => (
  <Layout title="Privacy Policy" updated="September 2026">
    <Section title="Overview">
      <p>
        Choose Me Properties LLC (&ldquo;we,&rdquo; &ldquo;us&rdquo;) is a Washington real estate acquisition and
        property research company. This policy explains what information this website collects, how we use it, and
        the choices you have.
      </p>
    </Section>
    <Section title="What we collect">
      <p>When you submit our inquiry form, we collect the information you choose to share, which may include:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Your name, email address, and phone number</li>
        <li>Your preferred contact method and preferred team member</li>
        <li>Property details you provide, such as an address, property type, ownership relationship, timing, occupancy, condition, and your goals</li>
        <li>Any questions or messages you write to us</li>
      </ul>
      <p>
        Our hosting platform also collects standard website analytics (such as pages visited and general device or
        browser information) to help us understand how the site is used.
      </p>
      <p>
        Please do not submit Social Security numbers, banking details, or sensitive documents through this website.
      </p>
    </Section>
    <Section title="How we use your information">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>To review and respond to your inquiry using your preferred contact method</li>
        <li>To research a property or opportunity you tell us about</li>
        <li>To keep an internal record of our conversations with you</li>
      </ul>
    </Section>
    <Section title="Where your information is stored">
      <p>
        Submissions are stored in our secure website database. Each inquiry is also sent to our team email inbox and
        recorded in HubSpot, the customer relationship management (CRM) system we use to organize conversations. In
        HubSpot, we match or create a contact by email address and attach a dated note for each inquiry; for property
        opportunities we may also create a deal record and a follow-up task for our team.
      </p>
    </Section>
    <Section title="What we do not do">
      <ul className="list-disc pl-6 space-y-1.5">
        <li>We do not sell your personal information</li>
        <li>Submitting the form does not subscribe you to marketing emails or text messages</li>
        <li>We do not share your information with third parties except the service providers that operate this website, email, and CRM</li>
      </ul>
    </Section>
    <Section title="Retention and security">
      <p>
        We keep inquiry records as long as needed to manage our relationship with you and maintain accurate business
        records. We use reasonable administrative and technical safeguards, and access credentials for our systems are
        stored securely and never embedded in the website itself.
      </p>
    </Section>
    <Section title="Your choices">
      <p>
        You may ask us to review, correct, or delete the information we hold about you by emailing{" "}
        <a href="mailto:contact@choosemeproperties.com" className="font-semibold text-[#A77A50] underline underline-offset-4">
          contact@choosemeproperties.com
        </a>{" "}
        or calling <a href="tel:+12064831705" className="font-semibold text-[#A77A50] underline underline-offset-4">(206) 483-1705</a>.
      </p>
    </Section>
    <Section title="Children">
      <p>This website is intended for adults and is not directed at children under 13.</p>
    </Section>
    <Section title="Changes to this policy">
      <p>
        If we update this policy, we will post the revised version on this page with a new &ldquo;last updated&rdquo;
        date.
      </p>
    </Section>
    <Section title="Contact">
      <p>
        Choose Me Properties LLC —{" "}
        <a href="mailto:contact@choosemeproperties.com" className="font-semibold text-[#A77A50] underline underline-offset-4">
          contact@choosemeproperties.com
        </a>{" "}
        — (206) 483-1705. Ask for Jeremy Alfred, Taj Alfred, or Robert Swearengen III.
      </p>
    </Section>
  </Layout>
);

export const TermsPage = () => (
  <Layout title="Terms of Use" updated="September 2026">
    <Section title="Acceptance of these terms">
      <p>
        By using this website, you agree to these Terms of Use. If you do not agree, please do not use the site.
      </p>
    </Section>
    <Section title="Informational purposes only">
      <p>
        This website is provided by Choose Me Properties LLC for general informational purposes. Content on this site
        describes our research and acquisition interests; it is not an offer to purchase any property, and submitting
        the inquiry form creates no obligation — for you or for us — to sell, buy, or proceed with any transaction.
      </p>
    </Section>
    <Section title="Not professional advice">
      <p>
        Nothing on this website is legal, tax, financial, or real estate advice, and it is not an appraisal or a
        valuation of your property. We do not make promises about property values, zoning outcomes, or development
        approvals. Before making decisions about your property, consider consulting your own attorney, accountant, or
        other qualified professional.
      </p>
    </Section>
    <Section title="Accuracy of information">
      <p>
        We work to keep this website accurate, but we do not guarantee that all content is complete or current. Any
        research we share in conversation reflects information available at the time and may change.
      </p>
    </Section>
    <Section title="Submitting information">
      <p>
        When you submit the inquiry form, you confirm that the information you provide is accurate to the best of your
        knowledge and that you are authorized to share it. You give us permission to contact you using your preferred
        method about your inquiry. Do not submit Social Security numbers, banking details, or sensitive documents
        through this website.
      </p>
    </Section>
    <Section title="Third-party services">
      <p>
        This website relies on third-party providers for hosting, email delivery, and customer relationship management
        (HubSpot). Your use of the site is also subject to those providers&rsquo; availability and policies.
      </p>
    </Section>
    <Section title="Intellectual property">
      <p>
        The content, design, and branding of this website belong to Choose Me Properties LLC and may not be copied or
        reused without permission.
      </p>
    </Section>
    <Section title="Limitation of liability">
      <p>
        To the fullest extent permitted by law, Choose Me Properties LLC is not liable for any indirect, incidental,
        or consequential damages arising from your use of this website or reliance on its content.
      </p>
    </Section>
    <Section title="Governing law">
      <p>These terms are governed by the laws of the State of Washington.</p>
    </Section>
    <Section title="Changes and contact">
      <p>
        We may update these terms from time to time; the current version will always be posted here. Questions? Email{" "}
        <a href="mailto:contact@choosemeproperties.com" className="font-semibold text-[#A77A50] underline underline-offset-4">
          contact@choosemeproperties.com
        </a>{" "}
        or call (206) 483-1705.
      </p>
    </Section>
  </Layout>
);
